/**
 * NADM Tire Supply — Theme Scripts
 *
 * Entrypoint for NADM custom JavaScript.
 * Layered on top of Horizon @theme/* modules (do not modify Horizon core scripts).
 *
 * Rules:
 * - ES module only
 * - Keep side effects explicit and minimal
 * - Avoid unnecessary JavaScript
 */

/** @typedef {{ width: number, aspect: number, diameter: number, query: string, score: number, odDelta: number }} TireAltSize */

const WIDTH_TOLERANCE_MM = 10;
const ASPECT_TOLERANCE = 5;
const DIAMETER_TOLERANCE = 0;
const MAX_ALT_SIZES = 8;
const MAX_ALT_PRODUCTS = 8;

/** Plausible metric tire ranges — reject ambiguous digit strings that are not sizes. */
const SIZE_WIDTH_MIN = 125;
const SIZE_WIDTH_MAX = 355;
const SIZE_ASPECT_MIN = 25;
const SIZE_ASPECT_MAX = 95;
const SIZE_DIAMETER_MIN = 10;
const SIZE_DIAMETER_MAX = 28;

/**
 * Escape text for safe HTML interpolation.
 * @param {unknown} value
 * @returns {string}
 */
function escapeHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * Build canonical metric size WWW/AARDD when width/aspect/diameter are in range.
 * @param {string} width
 * @param {string} aspect
 * @param {string} diameter
 * @returns {string | null}
 */
function formatCanonicalTireSize(width, aspect, diameter) {
  if (!/^\d{3}$/.test(width)) return null;
  if (!/^\d{2,3}$/.test(aspect)) return null;
  if (!/^\d{2}$/.test(diameter)) return null;

  const w = Number(width);
  const a = Number(aspect);
  const d = Number(diameter);

  if (w < SIZE_WIDTH_MIN || w > SIZE_WIDTH_MAX) return null;
  if (a < SIZE_ASPECT_MIN || a > SIZE_ASPECT_MAX) return null;
  if (d < SIZE_DIAMETER_MIN || d > SIZE_DIAMETER_MAX) return null;

  return `${width}/${aspect}R${diameter}`;
}

/**
 * Normalize free-text tire size input to canonical WWW/AARDD before search.
 * Returns null when the query is not an unambiguous tire size (brand/model/keywords).
 *
 * @param {unknown} raw
 * @returns {string | null}
 */
export function normalizeTireSizeQuery(raw) {
  if (raw == null) return null;

  const trimmed = String(raw).trim();
  if (!trimmed) return null;

  // Entire query must look like a size — reject multi-token keyword searches.
  // Allow digits, R/ZR, P/T/LT prefixes, XL/RF suffixes, and / - space separators only.
  if (!/^[0-9A-Za-z\s\-\/]+$/.test(trimmed)) return null;
  if (/[A-Za-z]{3,}/.test(trimmed.replace(/LT|ZR|XL|RF/gi, ''))) return null;

  let cleaned = trimmed
    .toUpperCase()
    .replace(/[\s\-]+/g, '')
    .replace(/ZR/g, 'R')
    .replace(/LT/g, '');

  if (/^[PT]\d/.test(cleaned)) {
    cleaned = cleaned.slice(1);
  }

  cleaned = cleaned.replace(/(XL|RF)$/g, '');

  /** @type {RegExpMatchArray | null} */
  let match =
    cleaned.match(/^(\d{3})\/(\d{2,3})R(\d{2})$/) ||
    cleaned.match(/^(\d{3})\/(\d{2,3})\/(\d{2})$/) ||
    cleaned.match(/^(\d{3})(\d{2})R(\d{2})$/) ||
    cleaned.match(/^(\d{3})(\d{3})R(\d{2})$/) ||
    cleaned.match(/^(\d{3})(\d{2})(\d{2})$/) ||
    cleaned.match(/^(\d{3})(\d{3})(\d{2})$/);

  if (!match) return null;

  return formatCanonicalTireSize(match[1], match[2], match[3]);
}

/**
 * Apply tire-size normalization to a search `q` input when unambiguous.
 * @param {HTMLInputElement} input
 * @returns {boolean} true when the value was rewritten
 */
function applyTireSizeNormalizationToInput(input) {
  const normalized = normalizeTireSizeQuery(input.value);
  if (!normalized || input.value === normalized) return false;
  input.value = normalized;
  return true;
}

/**
 * If the search results URL has an unnormalized tire-size `q`, replace it with
 * the canonical form so Shopify re-runs search against WWW/AARDD.
 * @returns {boolean} true when a redirect was triggered
 */
function redirectUnnormalizedTireSizeSearch() {
  if (typeof window === 'undefined' || typeof location === 'undefined') return false;

  const path = location.pathname.replace(/\/+$/, '') || '/';
  if (!path.endsWith('/search')) return false;

  const params = new URLSearchParams(location.search);
  const raw = params.get('q');
  if (raw == null) return false;

  const normalized = normalizeTireSizeQuery(raw);
  if (!normalized || raw === normalized) return false;

  params.set('q', normalized);
  const query = params.toString();
  location.replace(`${location.pathname}${query ? `?${query}` : ''}${location.hash || ''}`);
  return true;
}

/**
 * Normalize tire-size queries on search forms / inputs before Shopify search runs.
 * Does not alter brand, model, or other keyword searches.
 */
function initNadmTireSizeSearchNormalization() {
  if (typeof document === 'undefined') return;
  if (document.documentElement.dataset.nadmTireSizeNormBound === 'true') return;
  document.documentElement.dataset.nadmTireSizeNormBound = 'true';

  // Bookmarks / shared links with alternate size formatting
  if (redirectUnnormalizedTireSizeSearch()) return;

  // Form submit (header search "View all", search page form, etc.)
  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;
      if (form.matches('[data-nadm-tire-finder]')) return;

      const input = form.querySelector('input[name="q"]');
      if (!(input instanceof HTMLInputElement)) return;
      applyTireSizeNormalizationToInput(input);
    },
    true
  );

  // Enter in predictive search navigates via JS using the input value — normalize first.
  document.addEventListener(
    'keydown',
    (event) => {
      if (event.key !== 'Enter') return;
      const target = event.target;
      if (!(target instanceof HTMLInputElement) || target.name !== 'q') return;
      applyTireSizeNormalizationToInput(target);
    },
    true
  );

  // Predictive search reads the input on `input` — rewrite complete sizes in capture phase
  // so the subsequent Horizon handler searches the canonical value.
  document.addEventListener(
    'input',
    (event) => {
      const target = event.target;
      if (!(target instanceof HTMLInputElement) || target.name !== 'q') return;
      applyTireSizeNormalizationToInput(target);
    },
    true
  );
}

/**
 * Binds retail tire finder forms: combines Width / Aspect / Diameter into
 * a Shopify search query (e.g. 225/45R17) on submit.
 */
function initNadmTireFinders() {
  if (typeof document === 'undefined') return;

  const forms = document.querySelectorAll('[data-nadm-tire-finder]');
  if (!forms.length) return;

  forms.forEach((form) => {
    if (form.dataset.nadmTireFinderBound === 'true') return;
    form.dataset.nadmTireFinderBound = 'true';

    const widthSelect = form.querySelector('[data-nadm-tire-finder-width]');
    const aspectSelect = form.querySelector('[data-nadm-tire-finder-aspect]');
    const diameterSelect = form.querySelector('[data-nadm-tire-finder-diameter]');

    form.addEventListener('submit', (event) => {
      const width = widthSelect?.value?.trim();
      const aspect = aspectSelect?.value?.trim();
      const diameter = diameterSelect?.value?.trim();
      const queryInput = form.querySelector('[data-nadm-tire-finder-query]');

      if (!width || !aspect || !diameter) {
        event.preventDefault();
        (widthSelect || aspectSelect || diameterSelect)?.focus?.();
        return;
      }

      const query = `${width}/${aspect}R${diameter}`;

      if (queryInput) {
        queryInput.value = query;
      }

      // Strip helper fields so only type + q reach /search
      form.querySelectorAll('[name="nadm_width"], [name="nadm_aspect"], [name="nadm_diameter"]').forEach((field) => {
        field.disabled = true;
      });
    });
  });
}

/**
 * Overall diameter (inches) for ranking similar sizes.
 * OD ≈ diameter + 2 * (width_mm * aspect/100) / 25.4
 */
function overallDiameterInches(width, aspect, diameter) {
  return diameter + (2 * (width * (aspect / 100))) / 25.4;
}

/**
 * Builds ranked alternative sizes within configured tolerances.
 * Diameter stays exact (wheel fitment). Width ±10mm, aspect ±5.
 *
 * @param {number} width
 * @param {number} aspect
 * @param {number} diameter
 * @returns {TireAltSize[]}
 */
function buildAlternativeSizes(width, aspect, diameter) {
  /** @type {TireAltSize[]} */
  const alts = [];
  const targetOd = overallDiameterInches(width, aspect, diameter);

  for (let dw = -WIDTH_TOLERANCE_MM; dw <= WIDTH_TOLERANCE_MM; dw += 10) {
    for (let da = -ASPECT_TOLERANCE; da <= ASPECT_TOLERANCE; da += 5) {
      for (let dd = -DIAMETER_TOLERANCE; dd <= DIAMETER_TOLERANCE; dd += 1) {
        if (dw === 0 && da === 0 && dd === 0) continue;

        const nextWidth = width + dw;
        const nextAspect = aspect + da;
        const nextDiameter = diameter + dd;

        if (nextWidth < 175 || nextWidth > 335) continue;
        if (nextAspect < 30 || nextAspect > 85) continue;
        if (nextDiameter < 13 || nextDiameter > 24) continue;

        const score = Math.abs(dw) / 10 + Math.abs(da) / 5 + Math.abs(dd) * 3;
        const odDelta = Math.abs(overallDiameterInches(nextWidth, nextAspect, nextDiameter) - targetOd);

        alts.push({
          width: nextWidth,
          aspect: nextAspect,
          diameter: nextDiameter,
          query: `${nextWidth}/${nextAspect}R${nextDiameter}`,
          score,
          odDelta,
        });
      }
    }
  }

  alts.sort((a, b) => {
    if (a.score !== b.score) return a.score - b.score;
    if (a.odDelta !== b.odDelta) return a.odDelta - b.odDelta;
    return a.query.localeCompare(b.query);
  });

  return alts.slice(0, MAX_ALT_SIZES);
}

/**
 * Fetches product suggestions for a tire size via Shopify predictive search.
 * @param {string} query
 * @returns {Promise<object[]>}
 */
async function fetchProductsForSize(query) {
  const url = new URL('/search/suggest.json', window.location.origin);
  url.searchParams.set('q', query);
  url.searchParams.set('resources[type]', 'product');
  url.searchParams.set('resources[limit]', '4');
  url.searchParams.set('resources[options][fields]', 'title,product_type,variants.title,variants.sku');

  try {
    const response = await fetch(url.toString(), {
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) return [];
    const data = await response.json();
    return data?.resources?.results?.products || [];
  } catch {
    return [];
  }
}

/**
 * Renders alternative size chips and product links when no exact match exists.
 */
async function initNadmTireSearchAlternatives() {
  if (typeof document === 'undefined') return;

  const root = document.querySelector('[data-nadm-tire-search-status]');
  if (!root || root.dataset.exactMatch === 'true') return;
  if (root.dataset.nadmTireAltsBound === 'true') return;
  root.dataset.nadmTireAltsBound = 'true';

  const width = Number(root.dataset.width);
  const aspect = Number(root.dataset.aspect);
  const diameter = Number(root.dataset.diameter);
  const searchUrl = root.dataset.searchUrl || '/search';

  if (!width || !aspect || !diameter) return;

  const chipsEl = root.querySelector('[data-nadm-tire-alt-chips]');
  const productsEl = root.querySelector('[data-nadm-tire-alt-products]');
  const loadingEl = root.querySelector('[data-nadm-tire-alt-loading]');
  const alternatives = buildAlternativeSizes(width, aspect, diameter);

  if (chipsEl && alternatives.length) {
    chipsEl.hidden = false;
    chipsEl.innerHTML = alternatives
      .map((alt) => {
        const href = `${searchUrl}?type=product&q=${encodeURIComponent(alt.query)}`;
        return `<a class="nadm-tire-search-status__chip" href="${href}">${escapeHtml(alt.query)}</a>`;
      })
      .join('');
  }

  // When Shopify already returned a result grid, keep chips only — avoid a second product list.
  const hasGridResults = Boolean(document.querySelector('#ResultsList .product-grid__item'));
  if (!productsEl || hasGridResults) return;

  if (loadingEl) loadingEl.hidden = false;

  /** @type {Map<string, object>} */
  const seen = new Map();

  // Fetch alternate sizes in parallel (cap concurrency via slice) to cut wait time.
  const results = await Promise.all(
    alternatives.map(async (alt) => ({ alt, products: await fetchProductsForSize(alt.query) }))
  );

  for (const { alt, products } of results) {
    if (seen.size >= MAX_ALT_PRODUCTS) break;
    const sizeKey = alt.query.toUpperCase();
    for (const product of products) {
      if (!product?.url || seen.has(product.url)) continue;
      const haystack = `${product.title || ''} ${product.sku || ''}`.toUpperCase().replace(/\s+/g, '');
      if (!haystack.includes(sizeKey)) continue;
      seen.set(product.url, { product, size: alt.query, score: alt.score, odDelta: alt.odDelta });
      if (seen.size >= MAX_ALT_PRODUCTS) break;
    }
  }

  if (loadingEl) loadingEl.hidden = true;

  if (!seen.size) {
    const quoteUrl = escapeHtml(root.dataset.quoteUrl || '/pages/quote-request');
    productsEl.hidden = false;
    productsEl.innerHTML =
      `<p class="nadm-tire-search-status__empty">No close sizes are in stock online. <a href="${quoteUrl}">Request a quote</a> and we will check availability.</p>`;
    return;
  }

  const ranked = Array.from(seen.values()).sort((a, b) => {
    if (a.score !== b.score) return a.score - b.score;
    return a.odDelta - b.odDelta;
  });

  productsEl.hidden = false;
  productsEl.innerHTML = `
    <ul class="nadm-tire-search-status__list" role="list">
      ${ranked
        .map(({ product, size }) => {
          const title = escapeHtml(product.title || 'View product');
          const price = escapeHtml(product.price || '');
          const safeSize = escapeHtml(size);
          const href = escapeHtml(product.url);
          const image = product.image || product.featured_image || '';
          const safeImage = escapeHtml(image);
          return `
            <li class="nadm-tire-search-status__item">
              <a class="nadm-tire-search-status__product" href="${href}">
                ${
                  image
                    ? `<img class="nadm-tire-search-status__thumb" src="${safeImage}" alt="" width="72" height="72" loading="lazy" decoding="async">`
                    : ''
                }
                <span class="nadm-tire-search-status__product-copy">
                  <span class="nadm-tire-search-status__product-title">${title}</span>
                  <span class="nadm-tire-search-status__product-size">${safeSize}</span>
                  ${price ? `<span class="nadm-tire-search-status__product-price">${price}</span>` : ''}
                </span>
              </a>
            </li>
          `;
        })
        .join('')}
    </ul>
  `;
}

/**
 * Moves keyboard focus to form status regions after server-side validation.
 * Relies on tabindex="-1" already present on NADM / contact status blocks.
 */
function initNadmFormStatusFocus() {
  if (typeof document === 'undefined') return;

  const status = document.querySelector('[data-nadm-form-status], .contact-form__error[autofocus], .contact-form__success[autofocus], .email-signup__message[tabindex="-1"]');
  if (!(status instanceof HTMLElement)) return;

  // Avoid stealing focus if the user already moved into the form.
  if (document.activeElement && status.contains(document.activeElement)) return;
  if (document.activeElement && document.activeElement !== document.body && !status.hasAttribute('autofocus')) {
    return;
  }

  try {
    status.focus({ preventScroll: false });
  } catch {
    status.focus();
  }
}

/**
 * Initializes the NADM theme layer.
 */
export function initNadmTheme() {
  if (typeof window === 'undefined') return;

  initNadmTireSizeSearchNormalization();
  initNadmTireFinders();
  initNadmTireSearchAlternatives();
  initNadmFormStatusFocus();

  if (typeof Shopify !== 'undefined' && Shopify.designMode) {
    document.addEventListener('shopify:section:load', () => {
      initNadmTireFinders();
      const status = document.querySelector('[data-nadm-tire-search-status]');
      if (status) delete status.dataset.nadmTireAltsBound;
      initNadmTireSearchAlternatives();
    });
  }
}

initNadmTheme();
